import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, TextField, InputAdornment, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SearchResult from './components/SearchResult';
import ProjectHierarchyView from './components/ProjectHierarchyView';

/**
 * Projects route only: single search bar + mode switch by ?q=.
 * - With ?q= → Search Mode (<SearchResult />).
 * - Without ?q= → Projects grid (management view).
 * Search bar appears only on Projects, not on Developer or other inventory sections.
 */
const ProjectsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') ?? '';
  const [inputValue, setInputValue] = useState(searchQuery);
  const isSearchMode = Boolean(searchQuery?.trim());

  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const value = (inputValue ?? '').trim();
    if (value) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
  };

  const handleClearSearch = () => {
    setInputValue('');
    setSearchParams({});
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Search bar – visible in both modes */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          p: 2,
          pb: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <TextField
          placeholder="Search projects, property types, price or location…"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          size="small"
          sx={{
            minWidth: 280,
            flex: '1 1 280px',
            maxWidth: '80%',
            '& .MuiInputBase-root': { height: 40, borderRadius: 1, bgcolor: 'background.default' },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ color: 'text.secondary' }}>
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          variant="contained"
          size="small"
          sx={{
            height: 40,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: 'success.main',
            '&:hover': { bgcolor: 'success.dark' },
          }}
        >
          Search
        </Button>
        {isSearchMode && (
          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={handleClearSearch}
            sx={{
              height: 40,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': { borderColor: 'text.secondary', bgcolor: 'action.hover' },
            }}
          >
            Clear Search
          </Button>
        )}
      </Box>

      {isSearchMode ? (
        <SearchResult query={searchQuery} />
      ) : (
        <ProjectHierarchyView />
      )}
    </Box>
  );
};

export default ProjectsPage;
